<div class="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-gray-100 flex flex-col">

  <!-- ── Loading ──────────────────────────────────────────────────────────── -->
  @if (loading) {
    <div class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-3">
        <div class="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
        <p class="text-gray-500 text-sm font-medium">Cargando formulario...</p>
      </div>
    </div>
  }

  <!-- ── Error ─────────────────────────────────────────────────────────────── -->
  @else if (error) {
    <div class="flex-1 flex items-center justify-center p-4">
      <div class="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md w-full text-center">
        <div class="w-14 h-14 bg-error-50 text-error-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <h2 class="text-lg font-bold text-gray-900 mb-1">Formulario no disponible</h2>
        <p class="text-sm text-gray-500">Este enlace es inválido o el formulario ha sido desactivado.</p>
      </div>
    </div>
  }

  <!-- ── Success ───────────────────────────────────────────────────────────── -->
  @else if (submitSuccess) {
    <div class="flex-1 flex items-center justify-center p-4">
      <div class="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md w-full text-center">
        <div class="w-14 h-14 bg-success-50 text-success-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2">¡Gracias!</h2>
        <p class="text-gray-500 text-sm">{{ successMessage || 'Hemos recibido tu información correctamente.' }}</p>
      </div>
    </div>
  }

  <!-- ── Form ──────────────────────────────────────────────────────────────── -->
  @else if (formData) {

    <!-- Page header: logo + business name -->
    @if (formData.logoUrl || formData.businessName) {
      <header class="pt-8 pb-2 flex flex-col items-center gap-2 px-4">
        @if (formData.logoUrl) {
          <img [src]="formData.logoUrl" [alt]="formData.businessName || 'Logo'"
            class="h-12 w-auto object-contain" />
        }
        @if (formData.businessName) {
          <span class="text-xs font-medium text-gray-400 tracking-wide uppercase">{{ formData.businessName }}</span>
        }
      </header>
    }

    <!-- Main: form card -->
    <main class="flex-1 flex items-center justify-center px-4 py-8">
      <div class="w-full max-w-lg">

        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" [ngStyle]="containerStyle">

          <!-- Multi-step progress bar -->
          @if (isMultiStep) {
            <div class="px-8 pt-6 pb-0">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-semibold text-brand-500 uppercase tracking-wide">Paso {{ currentStep }} de {{ totalSteps }}</span>
                <span class="text-xs text-gray-400">{{ ((currentStep / totalSteps) * 100) | number:'1.0-0' }}%</span>
              </div>
              <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full bg-brand-500 rounded-full transition-all duration-500 ease-out"
                  [style.width]="((currentStep / totalSteps) * 100) + '%'"></div>
              </div>
            </div>
          }

          <!-- Form header: title + description -->
          <div class="px-8 pt-6 text-center">
            <h1 class="text-2xl font-bold text-gray-900 leading-tight">
              {{ stepTitle || formData.title || formData.name }}
            </h1>
            @if (stepDescription) {
              <p class="mt-2 text-sm text-gray-500 whitespace-pre-line leading-relaxed">{{ stepDescription }}</p>
            } @else if (!isMultiStep && formData.description) {
              <p class="mt-2 text-sm text-gray-500 whitespace-pre-line leading-relaxed">{{ formData.description }}</p>
            }
            @if (stepImageUrl) {
              <img [src]="stepImageUrl" alt="" class="mt-4 mx-auto max-h-40 w-auto rounded-xl object-contain" />
            }
          </div>

          <!-- Form fields -->
          <form [formGroup]="dynamicForm" (ngSubmit)="onSubmit()"
            class="px-8 pt-5 pb-8 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-5">

            @for (field of visibleFields; track field.id) {
              <div [class]="columnClass(field.columnSpan)">

                <!-- Label row (radio, checkboxes y gdpr_consent tienen el suyo propio) -->
                @if (field.fieldType !== 'checkboxes' && field.fieldType !== 'radio' && field.fieldType !== 'gdpr_consent') {
                  <div class="flex items-center gap-1.5 mb-1.5">
                    <label class="text-sm font-medium text-gray-700 leading-none">
                      {{ field.label }}
                      @if (isFieldRequired(field)) { <span class="text-error-500 ml-0.5">*</span> }
                    </label>
                    @if (field.helpText) {
                      <div class="relative group">
                        <button type="button"
                          class="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 font-bold italic font-serif text-[9px] leading-none transition-colors"
                          [ngClass]="activeHelpField === field.fieldKey
                            ? 'bg-brand-500 text-white'
                            : 'bg-gray-200 text-gray-500 hover:bg-indigo-100 hover:text-indigo-700'"
                          (click)="toggleHelp(field.fieldKey)"
                          [attr.aria-expanded]="activeHelpField === field.fieldKey"
                          aria-label="Más información sobre este campo">i</button>
                        <!-- Desktop: burbuja flotante en hover (oculta en móvil) -->
                        <div class="hidden sm:block absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-52
                                    bg-slate-800 text-slate-100 text-xs rounded-lg px-3 py-2 leading-relaxed
                                    shadow-lg pointer-events-none
                                    opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                             role="tooltip">
                          {{ field.helpText }}
                          <span class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></span>
                        </div>
                      </div>
                    }
                  </div>
                  <!-- Móvil: expande inline al tocar (oculto en desktop) -->
                  @if (field.helpText && activeHelpField === field.fieldKey) {
                    <div class="sm:hidden text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-2 leading-relaxed">
                      {{ field.helpText }}
                    </div>
                  }
                }

                <!-- ── Field inputs ───────────────────────────────────────── -->
                @switch (field.fieldType) {

                  @case ('text') {
                    <input type="text" [formControlName]="field.fieldKey"
                      [placeholder]="field.placeholder || ''"
                      [style.borderRadius]="inputBorderRadius"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-sm" />
                  }

                  @case ('email') {
                    <input type="email" [formControlName]="field.fieldKey"
                      [placeholder]="field.placeholder || ''"
                      [style.borderRadius]="inputBorderRadius"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-sm" />
                  }

                  @case ('phone') {
                    <input type="tel" [formControlName]="field.fieldKey"
                      [placeholder]="field.placeholder || ''"
                      [style.borderRadius]="inputBorderRadius"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-sm" />
                  }

                  @case ('number') {
                    <input type="number" [formControlName]="field.fieldKey"
                      [placeholder]="field.placeholder || ''"
                      [style.borderRadius]="inputBorderRadius"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-sm" />
                  }

                  @case ('date') {
                    <input type="date" [formControlName]="field.fieldKey"
                      [style.borderRadius]="inputBorderRadius"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-sm" />
                  }

                  @case ('textarea') {
                    <textarea [formControlName]="field.fieldKey"
                      [placeholder]="field.placeholder || ''" rows="3"
                      [style.borderRadius]="inputBorderRadius"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all resize-none text-sm"></textarea>
                  }

                  @case ('dropdown') {
                    <select [formControlName]="field.fieldKey"
                      [style.borderRadius]="inputBorderRadius"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-sm">
                      <option value="">{{ field.placeholder || 'Selecciona una opción' }}</option>
                      @for (opt of field.options; track opt.id) {
                        <option [value]="opt.optionValue">{{ opt.optionLabel }}</option>
                      }
                    </select>
                  }

                  @case ('radio') {
                    <fieldset>
                      <legend class="block text-sm font-medium text-gray-700 mb-2">
                        {{ field.label }}
                        @if (isFieldRequired(field)) { <span class="text-error-500 ml-0.5">*</span> }
                      </legend>
                      <div class="space-y-2">
                        @for (opt of field.options; track opt.id) {
                          <label class="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                            <input type="radio" [formControlName]="field.fieldKey" [value]="opt.optionValue"
                              class="w-4 h-4 border-gray-300 text-brand-500 focus:ring-brand-500" />
                            {{ opt.optionLabel }}
                          </label>
                        }
                      </div>
                    </fieldset>
                  }

                  @case ('checkboxes') {
                    <fieldset>
                      <legend class="block text-sm font-medium text-gray-700 mb-2">
                        {{ field.label }}
                        @if (isFieldRequired(field)) { <span class="text-error-500 ml-0.5">*</span> }
                      </legend>
                      <div class="space-y-2">
                        @for (opt of field.options; track opt.id) {
                          <label class="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox"
                              [checked]="isChecked(field.fieldKey, opt.optionValue)"
                              (change)="onCheckboxChange(field.fieldKey, opt.optionValue, $any($event.target).checked)"
                              class="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                            {{ opt.optionLabel }}
                          </label>
                        }
                      </div>
                    </fieldset>
                  }

                  @case ('file_upload') {
                    <div class="space-y-2">
                      @if (fileUploadValues.has(field.fieldKey)) {
                        <!-- Archivo subido correctamente -->
                        <div class="flex items-center gap-3 px-4 py-3 rounded-xl border border-brand-200 bg-brand-50 dark:border-brand-500/30 dark:bg-brand-500/10">
                          <svg class="w-5 h-5 flex-shrink-0 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-brand-700 dark:text-brand-300 truncate">
                              {{ fileUploadValues.get(field.fieldKey)?.name }}
                            </p>
                            <p class="text-xs text-brand-500">
                              {{ formatFileSize(fileUploadValues.get(field.fieldKey)?.size ?? 0) }}
                            </p>
                          </div>
                          <button type="button" (click)="removeFile(field.fieldKey)"
                            class="flex-shrink-0 text-brand-400 hover:text-error-500 transition-colors"
                            title="Eliminar archivo">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                      } @else if (isFileUploading(field.fieldKey)) {
                        <!-- Subiendo -->
                        <div class="flex flex-col items-center justify-center gap-2 w-full h-28 px-6 rounded-xl border-2 border-dashed border-brand-300 bg-brand-50 dark:border-brand-500/40 dark:bg-brand-500/10">
                          <div class="flex items-center gap-3">
                            <svg class="animate-spin w-5 h-5 text-brand-500" fill="none" viewBox="0 0 24 24">
                              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                            <p class="text-sm text-brand-600 dark:text-brand-400">
                              Subiendo archivo{{ uploadProgressOf(field.fieldKey) !== null ? ' ' + uploadProgressOf(field.fieldKey) + '%' : '...' }}
                            </p>
                          </div>
                          <!-- Barra de progreso: sin ella una subida lenta es indistinguible de una colgada. -->
                          @if (uploadProgressOf(field.fieldKey); as pct) {
                            <div class="w-full max-w-[220px] h-1.5 rounded-full bg-brand-100 dark:bg-brand-500/20 overflow-hidden">
                              <div class="h-full rounded-full bg-brand-500 transition-[width] duration-200" [style.width.%]="pct"></div>
                            </div>
                          }
                        </div>
                      } @else {
                        <!-- Zona de carga -->
                        <label class="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer bg-gray-50 dark:bg-gray-800/50 hover:border-brand-300 dark:hover:border-brand-500/50 hover:bg-brand-50 dark:hover:bg-brand-500/5 transition-colors">
                          <div class="flex flex-col items-center gap-1.5 pointer-events-none">
                            <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                            </svg>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Haz clic para subir un archivo</p>
                            <p class="text-xs text-gray-400 dark:text-gray-500">{{ field.placeholder || 'Imágenes y documentos · Máx. 10 MB' }}</p>
                          </div>
                          <input type="file" class="hidden"
                            accept=".jpg,.jpeg,.png,.pdf,.docx,.xlsx,.txt"
                            (change)="onFileSelected(field.fieldKey, $event)" />
                        </label>
                      }
                      @if (fileUploadErrors.get(field.fieldKey)) {
                        <p class="text-xs text-error-500 flex items-center gap-1">
                          <svg class="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                          </svg>
                          {{ fileUploadErrors.get(field.fieldKey) }}
                        </p>
                      }
                    </div>
                  }

                  @case ('gdpr_consent') {
                    <label class="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" [formControlName]="field.fieldKey"
                        class="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 flex-shrink-0" />
                      <span class="text-sm text-gray-600 leading-relaxed">
                        {{ field.label }}
                        @if (isFieldRequired(field)) { <span class="text-error-500 ml-0.5">*</span> }
                        @if (gdprPolicyText) {
                          &middot;
                          <button type="button" (click)="openPolicyModal()"
                            class="underline text-brand-600 hover:text-brand-700 transition-colors">{{ gdprLinkText }}</button>
                        } @else if (gdprPrivacyUrl) {
                          &middot;
                          <a [href]="gdprPrivacyUrl" target="_blank" rel="noopener noreferrer"
                            class="underline text-brand-600 hover:text-brand-700 transition-colors">{{ gdprLinkText }}</a>
                        }
                      </span>
                    </label>
                  }

                  @case ('rating_nps') {
                    <div class="space-y-2">
                      <div class="flex gap-1.5 flex-wrap">
                        @for (score of npsScores; track score) {
                          <button type="button"
                            (click)="dynamicForm.get(field.fieldKey)?.setValue(score.toString())"
                            class="w-9 h-9 rounded-lg text-sm font-medium border transition-colors"
                            [class.bg-brand-500]="dynamicForm.get(field.fieldKey)?.value === score.toString()"
                            [class.border-brand-500]="dynamicForm.get(field.fieldKey)?.value === score.toString()"
                            [class.text-white]="dynamicForm.get(field.fieldKey)?.value === score.toString()"
                            [class.bg-gray-50]="dynamicForm.get(field.fieldKey)?.value !== score.toString()"
                            [class.border-gray-200]="dynamicForm.get(field.fieldKey)?.value !== score.toString()"
                            [class.text-gray-700]="dynamicForm.get(field.fieldKey)?.value !== score.toString()">
                            {{ score }}
                          </button>
                        }
                      </div>
                      <div class="flex justify-between text-xs text-gray-400 px-0.5">
                        <span>Nada probable</span>
                        <span>Muy probable</span>
                      </div>
                    </div>
                  }

              @case ('geo_country_region') {
                <div class="space-y-2">
                  <app-country-select
                    [countries]="countries"
                    [value]="geoCountry(field.fieldKey)"
                    placeholder="País / región"
                    (valueChange)="onCountryChange(field.fieldKey, $event)" />
                  @if (regionsFor(geoCountry(field.fieldKey)).length > 0) {
                    <select
                      [value]="geoRegion(field.fieldKey)"
                      (change)="onRegionChange(field.fieldKey, $any($event.target).value)"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all">
                      <option value="">Selecciona una región</option>
                      @for (region of regionsFor(geoCountry(field.fieldKey)); track region) {
                        <option [value]="region">{{ region }}</option>
                      }
                    </select>
                  }
                </div>
              }

                  @default {
                    <input type="text" [placeholder]="field.placeholder || ''"
                      class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-sm" />
                  }

                }

                <!-- Validation error -->
                @if (hasError(field.fieldKey)) {
                  <p class="mt-1.5 text-xs text-error-500">{{ getFieldError(field.fieldKey) }}</p>
                }

              </div>
            }

            <!-- ── Error de envío ──────────────────────────────────────── -->
            @if (submitError) {
              <div class="col-span-1 sm:col-span-3">
                <div class="flex items-start gap-2.5 rounded-xl bg-error-50 border border-error-200 px-4 py-3">
                  <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                  <p class="text-sm text-error-700">{{ submitError }}</p>
                </div>
              </div>
            }

            <!-- ── Navegación / envío ───────────────────────────────────── -->
            <div class="col-span-1 sm:col-span-3 flex items-center gap-3 pt-1">
              @if (isMultiStep && currentStep > 1) {
                <button type="button" (click)="goPrevStep()"
                  class="flex-1 px-6 py-3 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors hover:bg-gray-50">
                  Anterior
                </button>
              }

              @if (!isLastStep) {
                <button type="button" (click)="goNextStep()"
                  class="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-xl transition-colors"
                  [ngStyle]="buttonStyle">
                  Siguiente
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              } @else {
                <button type="submit" [disabled]="isSubmitting"
                  class="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  [ngStyle]="buttonStyle">
                  @if (isSubmitting) {
                    <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Enviando...
                  } @else {
                    {{ submitLabel }}
                  }
                </button>
              }
            </div>

          </form>
        </div>

      </div>
    </main>

    <!-- Footer -->
    @if (formData.businessName) {
      <footer class="py-5 text-center space-y-1 px-4">
        <p class="text-xs text-gray-400">
          &copy; {{ currentYear }} {{ formData.businessName }} &middot; Todos los derechos reservados
        </p>
        @if (!formData.hideProviderBranding) {
          <p class="text-[11px] text-gray-300">
            Desarrollado con <span class="font-medium text-gray-400">PartnerTech CRM</span>
          </p>
        }
      </footer>
    }

  }

  <!-- ── Modal política de privacidad ──────────────────────────────────────── -->
  @if (policyModalOpen) {
    <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
         (click)="closePolicyModal()">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <!-- Panel -->
      <div class="relative z-10 bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[85vh]"
           (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 class="text-sm font-semibold text-gray-900">{{ gdprLinkText }}</h3>
          <button type="button" (click)="closePolicyModal()"
            class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Cerrar">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <!-- Contenido scrolleable -->
        <div class="px-6 py-5 overflow-y-auto flex-1">
          <p class="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{{ gdprPolicyText }}</p>
        </div>
        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button type="button" (click)="closePolicyModal()"
            class="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  }

</div>
